import React, { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
// Import Helpers
import { Helpers } from 'services';
// Import Enums
import { CommunicationType, CommunicationStatus } from 'enums';
// Import Constants
import { l } from 'constants/common';
// import UI Components
import { Popover, Tag } from '@geneui/components';
// Import Styles
import styles from './communicationCountItem.module.scss';

const { clearEmptyValuesFromObject } = Helpers;

interface Props {
    count: number;
    stats: Record<CommunicationType, Record<CommunicationStatus, number>>;
}

const CommunicationCountItem: FC<Props> = ({ count, stats }) => {
    const { t } = useTranslation();

    const convertedStats = useMemo(() => {
        const clearedStats = clearEmptyValuesFromObject({
            [CommunicationType[CommunicationType.Unknown]]: stats[CommunicationType.Unknown],
            [CommunicationType[CommunicationType.Email]]: stats[CommunicationType.Email],
            [CommunicationType[CommunicationType.SMS]]: stats[CommunicationType.SMS],
            [CommunicationType[CommunicationType.PushNotification]]: stats[CommunicationType.PushNotification],
            [CommunicationType[CommunicationType.PopUpNotification]]: stats[CommunicationType.PopUpNotification],
            [CommunicationType[CommunicationType.InternalMessage]]: stats[CommunicationType.InternalMessage],
            [CommunicationType[CommunicationType.Telegram]]: stats[CommunicationType.Telegram],
            [CommunicationType[CommunicationType.CMSPopUpNotification]]: stats[CommunicationType.CMSPopUpNotification],
            [CommunicationType[CommunicationType.Viber]]: stats[CommunicationType.Viber],
            [CommunicationType[CommunicationType.Hoory]]: stats[CommunicationType.Hoory],
            [CommunicationType[CommunicationType.FastexNotification]]: stats[CommunicationType.FastexNotification],
            [CommunicationType[CommunicationType.DynamicPopUp]]: stats[CommunicationType.DynamicPopUp],
            [CommunicationType[CommunicationType.YoPhone]]: stats[CommunicationType.YoPhone],

            [CommunicationType[CommunicationType.Bonus]]: stats[CommunicationType.Bonus],
        });

        return Object.entries(clearedStats).map(([title, values]) => {
            const newValues = clearEmptyValuesFromObject({
                [CommunicationStatus[CommunicationStatus.Succeeded]]: values[CommunicationStatus.Succeeded],
                [CommunicationStatus[CommunicationStatus.Failed]]: values[CommunicationStatus.Failed],
                [CommunicationStatus[CommunicationStatus.Pending]]: values[CommunicationStatus.Pending],
                [CommunicationStatus[CommunicationStatus.Queued]]: values[CommunicationStatus.Queued],
                [CommunicationStatus[CommunicationStatus.Skipped]]: values[CommunicationStatus.Skipped],
            });

            return {
                title,
                values: Object.entries(newValues).map(([status, count]) => ({
                    status,
                    count,
                })),
            };
        });
    }, [stats]);

    const countBlock = <Tag name={`${count}`} cornerRadius="smooth-radius" appearance="outline" size="medium" />;

    return (
        <>
            {isEmpty(stats) ? (
                countBlock
            ) : (
                <Popover
                    behave="toggle"
                    fullHeight={true}
                    extendTargetWidth={false}
                    align="start"
                    position="left"
                    cornerRadius="full-radius"
                    Content={
                        <div className={styles.container}>
                            {convertedStats.map(({ title, values }) => (
                                <div key={title}>
                                    <div className={styles.title}>{t(l[title])}</div>

                                    <div>
                                        {values.map(({ status, count }) => (
                                            <div key={status} className={styles.statusCountRow}>
                                                <div className={styles.status}>{t(l[status])}</div>
                                                <div className={styles.count}>{count}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    }
                >
                    {countBlock}
                </Popover>
            )}
        </>
    );
};

export default memo(CommunicationCountItem);
